import Seo from 'src/components/Seo'
import { seoDescription, seoText } from 'src/functions/seo'

// https://www.npmjs.com/package/react-schemaorg
// https://stackoverflow.com/questions/47417102/schema-org-structured-data-in-visible-elements
import { BreadcrumbList, Product, ProductGroup, WithContext } from 'schema-dts'
import { JsonLd } from 'src/components/JsonLd'

/* Types */
import { tProduct, combination as tCombination } from 'src/shared/frontendTypes/product'
import type { BreadcrumbItem } from 'src/shared/frontendTypes/product'

/* Functions */
import productUrl, { productVariantUrl } from 'src/functions/productUrl'
import { saveUrl } from 'src/functions/regex'
import getImages from 'src/pages/Product/functions/getImages'
import getImageUrl from 'src/pages/Product/functions/imageUrl'

/* Config */
import config from 'src/config'
import shopInfos from 'template/infos'
const { name, url } = shopInfos
const shopUrl = url.replace(/\/$/, '')
const merchantReturnPolicyId = `${shopUrl}#merchant-return-policy`

const TECHNICAL_TAG_REGEX =
  /^cs-(serie|hersteller|farbe|teil)(?:-(?:[0-9]+(?:-[0-9]+)*|base))?:(.+)$/i

const normalizeText = (value?: string | number | null) =>
  String(value ?? '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const absoluteProductUrl = (
  id: number,
  path: string,
  options?: number[],
  combinationKey?: string,
) => `${shopUrl}${combinationKey
  ? productVariantUrl(id, path, combinationKey)
  : productUrl(id, path, options)}`

const absoluteProductBaseUrl = (
  id: number,
  path: string,
) => `${shopUrl}${productUrl(id, path)}`

const absoluteBreadcrumbHref = (href?: string) => {
  if (!href) return shopUrl
  if (/^https?:\/\//i.test(href)) return href
  return `${shopUrl}${href.startsWith('/') ? href : `/${href}`}`
}

const uniqueValues = (values: Array<string | undefined | null>) => {
  const seen = new Set<string>()
  const result: string[] = []

  for (const value of values) {
    const display = normalizeText(value)
    const key = display.toLowerCase()
    if (!display || seen.has(key)) continue
    seen.add(key)
    result.push(display)
  }

  return result
}

const getKeywordDisplayValue = (keyword: string) => {
  const cleanKeyword = normalizeText(keyword)
  const facetMatch = cleanKeyword.match(/^([^:]{2,30}):(.+)$/)
  return normalizeText(facetMatch?.[2] || cleanKeyword)
}

const schemaProductGroupId = (id: number, path: string) =>
  `${absoluteProductBaseUrl(id, path)}#productgroup`

const schemaProductId = ({
  id,
  path,
  combinationKey,
  options,
  sku,
  mpn,
  ean,
}: {
  id: number
  path: string
  combinationKey?: string
  options?: number[]
  sku?: string
  mpn?: string
  ean?: string
}) => {
  const variantToken =
    normalizeText(combinationKey)
    || (options && options.length > 0 ? options.join('-') : '')
    || normalizeText(sku)
    || normalizeText(mpn)
    || normalizeText(ean)
    || 'default'

  return `${absoluteProductBaseUrl(id, path)}#product-${saveUrl(variantToken)}`
}

const cleanKeywords = (keywords?: string[]) =>
  uniqueValues(
    (keywords || [])
      .filter(keyword => !TECHNICAL_TAG_REGEX.test(keyword))
      .map(getKeywordDisplayValue),
  )

const getFacetValues = (keywords: string[], facet: string) =>
  uniqueValues(
    keywords.flatMap(keyword => {
      const match = keyword.match(/^([^:]{2,30}):(.+)$/)
      return match?.[1]?.trim().toLowerCase() === facet
        ? [match[2]]
        : []
    }),
  )

const getTechnicalValue = (technicalData: string[] | undefined, labels: RegExp[]) => {
  for (const line of technicalData || []) {
    const match = normalizeText(line).match(/^([^:=]+)[:=](.+)$/)
    if (!match) continue

    const label = normalizeText(match[1]).toLowerCase()
    if (labels.some(pattern => pattern.test(label))) return normalizeText(match[2])
  }

  return undefined
}

const GENERIC_VARIANT_VALUE_REGEX =
  /^(standard|standardausfuehrung|standardausführung|ausfuehrung|ausführung|variante|modell|default)$/i

const COLOR_LABELS = [/\bfarbe\b|\bfarbton\b|\bcolor\b|\bcolour\b/]

const COLOR_ALIASES = [
  { value: 'Bordeauxrot', patterns: [/\bbordeaux(?:\s*rot)?\b/i, /\bburgund(?:y)?\b/i] },
  { value: 'Anthrazit', patterns: [/\banthrazit\b/i, /\banthracite\b/i] },
  { value: 'Schwarz', patterns: [/\bschwarz\b/i, /\bblack\b/i] },
  { value: 'Weiss', patterns: [/\bweiss\b/i, /\bweiß\b/i, /\bwhite\b/i] },
  { value: 'Graphit', patterns: [/\bgraphit\b/i, /\bgraphite\b/i] },
  { value: 'Grau', patterns: [/\bgrau\b/i, /\bgrey\b/i, /\bgray\b/i] },
  { value: 'Hellgrau', patterns: [/\bhellgrau\b/i, /\blight\s*gr[ae]y\b/i] },
  { value: 'Dunkelgrau', patterns: [/\bdunkelgrau\b/i, /\bdark\s*gr[ae]y\b/i] },
  { value: 'Braun', patterns: [/\bbraun\b/i, /\bbrown\b/i] },
  { value: 'Beige', patterns: [/\bbeige\b/i] },
  { value: 'Creme', patterns: [/\bcreme\b/i, /\bcream\b/i, /\boff\s*white\b/i] },
  { value: 'Natur', patterns: [/\bnatur\b/i, /\bnatural\b/i] },
  { value: 'Eiche', patterns: [/\beiche\b/i, /\boak\b/i] },
  { value: 'Buche', patterns: [/\bbuche\b/i, /\bbeech\b/i] },
  { value: 'Nussbaum', patterns: [/\bnussbaum\b/i, /\bwalnut\b/i] },
  { value: 'Rot', patterns: [/\brot\b/i, /\bred\b/i] },
  { value: 'Orange', patterns: [/\borange\b/i] },
  { value: 'Gelb', patterns: [/\bgelb\b/i, /\byellow\b/i] },
  { value: 'Gruen', patterns: [/\bgruen\b/i, /\bgrün\b/i, /\bgreen\b/i] },
  { value: 'Blau', patterns: [/\bblau\b/i, /\bblue\b/i] },
  { value: 'Rosa', patterns: [/\brosa\b/i, /\bpink\b/i] },
  { value: 'Violett', patterns: [/\bviolett\b/i, /\blila\b/i, /\bpurple\b/i] },
  { value: 'Taupe', patterns: [/\btaupe\b/i] },
  { value: 'Gold', patterns: [/\bgold\b/i] },
  { value: 'Silber', patterns: [/\bsilber\b/i, /\bsilver\b/i] },
  { value: 'Chrom', patterns: [/\bchrom\b/i, /\bchrome\b/i] },
  { value: 'Messing', patterns: [/\bmessing\b/i, /\bbrass\b/i] },
  { value: 'Kupfer', patterns: [/\bkupfer\b/i, /\bcopper\b/i] },
] as const

const normalizeSchemaValue = (value?: string) => {
  const cleanValue = normalizeText(value)
  if (!cleanValue || GENERIC_VARIANT_VALUE_REGEX.test(cleanValue)) return undefined
  return cleanValue
}

const detectColorValue = (value?: string) => {
  const cleanValue = normalizeSchemaValue(value)
  if (!cleanValue) return undefined

  for (const color of COLOR_ALIASES) {
    if (color.patterns.some(pattern => pattern.test(cleanValue))) return color.value
  }

  return undefined
}

const getAttributeValue = (attributes: string | undefined, labels: RegExp[]) => {
  for (const part of (attributes || '').split(/[,|]+/g)) {
    const match = normalizeText(part).match(/^([^:=]+)[:=](.+)$/)
    if (!match) continue

    const label = normalizeText(match[1]).toLowerCase()
    if (labels.some(pattern => pattern.test(label))) return normalizeText(match[2])
  }

  return undefined
}

const getLabeledValueFromText = (value: string | undefined, labels: RegExp[]) => {
  const text = normalizeText(value)
  if (!text) return undefined

  for (const label of labels) {
    const match = text.match(new RegExp(`(?:^|[\\s,.;|])(${label.source})\\s*(?:[:=\\-]|ist|in|im|:)?\\s*([^,.;|()]{2,40})`, 'i'))
    const result = normalizeSchemaValue(match?.[2])
    if (result) return result
  }

  return undefined
}

const getColorValue = ({
  attributes,
  keywords,
  technicalData,
  description,
  name,
}: {
  attributes?: string
  keywords?: string[]
  technicalData?: string[]
  description?: string
  name?: string
}) => {
  const rawKeywords = keywords || []
  const attributeColor =
    normalizeSchemaValue(getAttributeValue(attributes, COLOR_LABELS))
    || detectColorValue(attributes)

  if (attributeColor) return attributeColor

  const labeledColor =
    normalizeSchemaValue(getFacetValues(rawKeywords, 'farbe')[0])
    || normalizeSchemaValue(getFacetValues(rawKeywords, 'farbton')[0])
    || normalizeSchemaValue(getTechnicalValue(technicalData, COLOR_LABELS))
    || normalizeSchemaValue(getLabeledValueFromText(description, COLOR_LABELS))

  if (labeledColor) return labeledColor

  return detectColorValue(name)
    || detectColorValue(description)
    || detectColorValue(rawKeywords.join(' '))
    || detectColorValue((technicalData || []).join(' '))
}

const buildAdditionalProperties = ({
  technicalData,
  keywords,
  weight,
}: {
  technicalData?: string[]
  keywords: string[]
  weight?: number
}) => {
  const entries = [
    ...(technicalData || []).flatMap(line => {
      const match = normalizeText(line).match(/^([^:=]+)[:=](.+)$/)
      if (!match) return []

      return [{
        '@type': 'PropertyValue',
        name: normalizeText(match[1]),
        value: normalizeText(match[2]),
      }]
    }),
    ...getFacetValues(keywords, 'serie').map(value => ({
      '@type': 'PropertyValue',
      name: 'Serie',
      value,
    })),
    ...getFacetValues(keywords, 'raum').map(value => ({
      '@type': 'PropertyValue',
      name: 'Raum',
      value,
    })),
    ...getFacetValues(keywords, 'stil').map(value => ({
      '@type': 'PropertyValue',
      name: 'Stil',
      value,
    })),
    ...(typeof weight === 'number'
      ? [{
        '@type': 'PropertyValue',
        name: 'Gewicht',
        value: String(weight),
      }]
      : []),
  ]

  const seen = new Set<string>()
  return entries.filter(entry => {
    const key = `${entry.name}:${entry.value}`.toLowerCase()
    if (!entry.name || !entry.value || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const buildGtinFields = (ean?: string) => {
  const digits = normalizeText(ean).replace(/\D/g, '')
  if (![8, 12, 13, 14].includes(digits.length)) return {}

  return {
    gtin: digits,
    ...(digits.length === 8 ? { gtin8: digits } : {}),
    ...(digits.length === 12 ? { gtin12: digits } : {}),
    ...(digits.length === 13 ? { gtin13: digits } : {}),
    ...(digits.length === 14 ? { gtin14: digits } : {}),
  }
}

const normalizePrice = (value: unknown) => {
  const price = Number(value)
  return Number.isFinite(price) && price > 0
    ? Number(price.toFixed(2))
    : undefined
}

const normalizeDate = (value?: string) => {
  if (!value) return undefined
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return undefined
  // Keep sale cutoffs precise instead of dropping the time and timezone.
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : date.toISOString()
}

const normalizeStock = (value: unknown) => {
  const stock = Number(value)
  return Number.isFinite(stock) && stock >= 0
    ? Math.floor(stock)
    : undefined
}

const getAvailability = ({
  onlyStock,
  stockLevel,
}: {
  onlyStock?: boolean
  stockLevel?: number
}) => {
  if (typeof stockLevel === 'number' && stockLevel > 0) {
    return onlyStock
      ? 'https://schema.org/LimitedAvailability'
      : 'https://schema.org/InStock'
  }

  return onlyStock
    ? 'https://schema.org/OutOfStock'
    : 'https://schema.org/BackOrder'
}

const getShippingTypeConfig = (shippingType?: number) => {
  const shippingSettings = config.structuredData.shipping
  const shippingTypes =
    shippingSettings.shippingTypes as Record<number, { name: string; rate: keyof typeof config.versandkosten }>

  return shippingTypes[shippingType || shippingSettings.defaultShippingType]
    || shippingTypes[shippingSettings.defaultShippingType]
}

const buildShippingDetails = ({
  shippingType,
  deliverytime,
  stockLevel,
}: {
  shippingType?: number
  deliverytime?: number
  stockLevel?: number
}) => {
  const shippingTypeConfig = getShippingTypeConfig(shippingType)
  if (!shippingTypeConfig) return undefined

  const shippingSettings = config.structuredData.shipping
  const shippingRate = config.versandkosten[shippingTypeConfig.rate]
  const transitDays =
    typeof stockLevel === 'number' && stockLevel > 0
      ? config.stockDeliveryTime
      : normalizeStock(deliverytime)

  return compactObject({
    '@type': 'OfferShippingDetails',
    shippingRate: {
      '@type': 'MonetaryAmount',
      value: shippingRate,
      currency: 'EUR',
    },
    shippingDestination: {
      '@type': 'DefinedRegion',
      addressCountry: shippingSettings.destinationCountry,
    },
    deliveryTime: {
      '@type': 'ShippingDeliveryTime',
      handlingTime: {
        '@type': 'QuantitativeValue',
        minValue: shippingSettings.handlingTime.minValue,
        maxValue: shippingSettings.handlingTime.maxValue,
        unitCode: shippingSettings.handlingTime.unitCode,
      },
      transitTime: transitDays === undefined ? undefined : {
        '@type': 'QuantitativeValue',
        minValue: transitDays,
        maxValue: transitDays,
        unitCode: shippingSettings.handlingTime.unitCode,
      },
    },
  })
}

const buildProductDescription = (product: Pick<tProduct, 'description' | 'descriptionDetails' | 'descriptionShort'>) =>
  seoText(product.descriptionDetails)
  || seoText(product.descriptionShort)
  || seoText(product.description)

const getVariantDimensions = (product: tProduct) => {
  const combinations = product.combinations.filter(combination => combination.cultivable !== false)
  const dimensions = (product.attributes || []).flatMap(attribute => {
    const optionIds = new Set(attribute.options.map(option => option.id))
    const selectedOptions = new Set(combinations.flatMap(combination =>
      combination.options.filter(optionId => optionIds.has(optionId))))
    if (selectedOptions.size < 2) return []

    const attributeName = normalizeText(attribute.name).toLowerCase()

    if (/farbe|color/.test(attributeName)) return ['https://schema.org/color']
    if (/groesse|größe|grosse|size|mass|maß/.test(attributeName)) return ['https://schema.org/size']
    if (/material|stoff|gewebe|holzart/.test(attributeName)) return ['https://schema.org/material']
    if (/muster|pattern|motiv/.test(attributeName)) return ['https://schema.org/pattern']

    return []
  })

  const variantColors = uniqueValues(combinations
    .map(combination => getColorValue({ attributes: combination.attributes.join(', ') })))

  if (variantColors.length > 1) {
    dimensions.push('https://schema.org/color')
  }

  return uniqueValues(dimensions)
}

const compactObject = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    const items = value
      .map(compactObject)
      .filter(item => item !== undefined && !(Array.isArray(item) && item.length === 0))

    return items.length > 0 ? items : undefined
  }

  if (value === undefined || value === null || value === '') return undefined
  if (typeof value !== 'object') return value

  const result = Object.fromEntries(
    Object.entries(value)
      .map(([key, entry]) => [key, compactObject(entry)])
      .filter(([, entry]) => entry !== undefined),
  )

  return Object.keys(result).length > 0 ? result : undefined
}

interface HeaderProps {
  id: number
  path: string
  metaTitle: string
  productName?: string
  descriptionShort: string
  descriptionDetails?: string
  description?: string
  keywords?: string[]
  images?: tProduct['images']
}

export const SeoHeader: React.FC<HeaderProps> = ({
  id,
  path,
  metaTitle,
  productName,
  descriptionShort,
  descriptionDetails,
  description,
  keywords,
  images,
}) => {
  const cleanMetaKeywords = cleanKeywords(keywords)
  const pageTitle = seoText(metaTitle) || seoText(productName) || `Produkt ${id}`
  const pageDescription = seoDescription(
    seoText(descriptionShort) || seoText(descriptionDetails) || seoText(description),
    `${pageTitle}: Produktinformationen und verfügbare Ausführungen bei ${name}.`,
  )
  // Every variant is selectable on this same product page. Its canonical
  // identifies the group; individual offer URLs still preselect each variant.
  const pageUrl = absoluteProductBaseUrl(id, path)
  const socialImage = (images || [])
    .map(image => getImageUrl(image, id, 'xxl'))
    .find((image): image is string => Boolean(image))

  return <Seo
    title={pageTitle}
    description={pageDescription}
    canonical={pageUrl}
    image={socialImage}
    imageAlt={seoText(productName) || pageTitle}
    type='product'
    keywords={cleanMetaKeywords.join(', ') || undefined}
  />
}

/* ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

export const getProductBreadcrumb = (product: tProduct): BreadcrumbItem[] =>
  product.breadcrumb?.length
    ? product.breadcrumb
    : [
      ...(product.brand && product.brandTitel
        ? [{
          title: product.brandTitel,
          href: `/b/${saveUrl(product.brandTitel)}/${product.brand}`,
        }]
        : []),
      {
        title: product.metaTitle || product.name,
        href: productUrl(product.id, product.path),
      },
    ]

export const SeoProductBreadcrumb: React.FC<{ product: tProduct }> = ({ product }) => {
  const entries = [
    {
      title: 'Home',
      href: shopUrl,
    },
    ...getProductBreadcrumb(product),
  ]

  const item = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: entries.map((entry, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: entry.title,
      item: absoluteBreadcrumbHref(entry.href),
    })),
  } as WithContext<BreadcrumbList>

  return <JsonLd<BreadcrumbList> item={item} />
}

/* ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

interface ProductCombination {
  id: number
  path: string
  name: string
  description: string
  brandTitel: string
  onlyStock?: boolean
  inquireOnly?: boolean
  hook?: string
  productType?: string
  keywords?: string[]
  technicalData?: string[]

  attributes?: string
  schemaAttributes?: string
  images: string[]
  price: number
  regularPrice?: number
  priceValidUntil?: string
  deliverytime?: number
  shippingType?: number
  combinationKey?: string
  options?: number[]
  ean?: string
  sku?: string
  mpn?: string
  originCountry?: string
  weight?: number
  stock?: number
  includeVariantOf?: boolean
  includeBrand?: boolean
  withContext?: boolean
}

const buildProductCombinationItem = ({
  id,
  path,
  name,
  description,
  brandTitel,
  onlyStock,
  inquireOnly,
  hook,
  productType,
  keywords,
  technicalData,
  images,
  price,
  regularPrice,
  priceValidUntil,
  deliverytime,
  shippingType,
  attributes,
  schemaAttributes,
  options,
  ean,
  sku,
  mpn,
  originCountry,
  weight,
  stock,
  combinationKey,
  includeVariantOf,
  includeBrand = true,
  withContext = true,
}: ProductCombination) => {
  const fullUrl = absoluteProductUrl(id, path, options, combinationKey)
  const productSchemaId = schemaProductId({
    id,
    path,
    combinationKey,
    options,
    sku,
    mpn,
    ean,
  })
  const rawSchemaKeywords = (keywords || []).filter(keyword => !TECHNICAL_TAG_REGEX.test(keyword))
  const cleanSchemaKeywords = cleanKeywords(keywords)
  const brandName =
    normalizeText(brandTitel)
    || getFacetValues(rawSchemaKeywords, 'marke')[0]
    || getFacetValues(rawSchemaKeywords, 'brand')[0]
  const activePrice = normalizePrice(price)
  const originalPrice = normalizePrice(regularPrice)
  const hasStrikethroughPrice = Boolean(originalPrice && activePrice && originalPrice > activePrice)
  const resolvedPriceValidUntil = normalizeDate(priceValidUntil)
  const stockLevel = normalizeStock(stock)
  const attributesForSchema = schemaAttributes || attributes
  const color =
    getColorValue({
      attributes: attributesForSchema,
      keywords: rawSchemaKeywords,
      technicalData,
      description,
      name,
    })
  const material =
    getFacetValues(rawSchemaKeywords, 'material')[0]
    || getTechnicalValue(technicalData, [/material|stoff|gewebe|holzart/])
  const size =
    getAttributeValue(attributesForSchema, [/groesse|größe|grosse|size|mass|maß/])
    || getFacetValues(rawSchemaKeywords, 'groesse')[0]
    || getTechnicalValue(technicalData, [/groesse|größe|grosse|size|mass|maß|abmessung/])
  const pattern =
    getAttributeValue(attributesForSchema, [/muster|pattern|motiv/])
    || getFacetValues(rawSchemaKeywords, 'muster')[0]
    || getTechnicalValue(technicalData, [/muster|pattern|motiv/])
  const category =
    normalizeText(productType)
    || getFacetValues(rawSchemaKeywords, 'produkttyp')[0]
    || getFacetValues(rawSchemaKeywords, 'kategorie')[0]
  const additionalProperty = buildAdditionalProperties({
    technicalData,
    keywords: rawSchemaKeywords,
    weight,
  })

  return compactObject({
    '@context': withContext ? 'https://schema.org' : undefined,
    '@type': 'Product',
    '@id': productSchemaId,

    name: attributes ? `${name} - ${attributes}` : name,
    description: normalizeText(description),
    slogan: normalizeText(hook),
    image: uniqueValues(images),
    keywords: cleanSchemaKeywords.length > 0 ? cleanSchemaKeywords.join(', ') : undefined,
    category,
    color,
    material,
    size,
    pattern,
    additionalProperty,

    ...buildGtinFields(ean),
    productID: String(id),
    inProductGroupWithID: String(id),
    sku: normalizeText(sku),
    mpn: normalizeText(mpn),
    model: normalizeText(attributes),
    countryOfOrigin: normalizeText(originCountry),
    url: fullUrl,
    isVariantOf: includeVariantOf ? { '@id': schemaProductGroupId(id, path) } : undefined,

    brand: includeBrand && brandName ? {
      '@type': 'Brand',
      name: brandName,
    } : undefined,

    // An inquiry is not an online purchase offer or proof of store availability.
    offers: activePrice && !inquireOnly ? {
      '@type': 'Offer',
      price: activePrice,
      priceCurrency: 'EUR',
      priceSpecification: hasStrikethroughPrice ? {
        '@type': 'UnitPriceSpecification',
        priceType: 'https://schema.org/StrikethroughPrice',
        price: originalPrice,
        priceCurrency: 'EUR',
      } : undefined,
      itemCondition: 'https://schema.org/NewCondition',
      availability: getAvailability({ onlyStock, stockLevel }),
      inventoryLevel: typeof stockLevel === 'number' ? {
        '@type': 'QuantitativeValue',
        value: stockLevel,
      } : undefined,
      shippingDetails: buildShippingDetails({
        shippingType,
        deliverytime,
        stockLevel,
      }),
      hasMerchantReturnPolicy: { '@id': merchantReturnPolicyId },
      priceValidUntil: resolvedPriceValidUntil,
      url: fullUrl,
    } : undefined,
  }) as WithContext<Product>
}

export const SeoProductCombination: React.FC<ProductCombination> = (props) => {
  const item = buildProductCombinationItem(props)

  return <JsonLd<Product> item={item} />
}

const getCombinationSchemaAttributes = (product: tProduct, combination: tCombination) => {
  const labeledAttributes = (combination.options || []).flatMap(optionId => {
    const attribute = product.attributes?.find(entry =>
      entry.options.some(option => option.id === optionId),
    )
    const option = attribute?.options.find(entry => entry.id === optionId)

    return attribute && option
      ? [`${attribute.name}: ${option.name}`]
      : []
  })

  return labeledAttributes.length > 0
    ? labeledAttributes
    : combination.attributes
}

const buildProductGroupVariant = (product: tProduct, combination: tCombination, description: string) => buildProductCombinationItem({
  id: product.id,
  path: product.path,
  name: product.name,
  description,
  brandTitel: product.brandTitel,
  onlyStock: product.onlyStock || product.attributes?.some(attribute =>
    attribute.options.some(option => combination.options.includes(option.id) && option.uncultivable)),
  inquireOnly: product.purchaseMode ? product.purchaseMode === 'inquiry' : product.inquireOnly,
  hook: product.hook,
  productType: product.productType,
  keywords: product.keywords,
  technicalData: product.technicalData,
  images: getImages(product, combination, combination.options)
    .map(image => getImageUrl(image, product.id, 'xxl'))
    .filter((image): image is string => Boolean(image)),
  attributes: combination.attributes.join(', '),
  schemaAttributes: getCombinationSchemaAttributes(product, combination).join(', '),
  price: combination.discount ? combination.discount.price : combination.price,
  regularPrice: combination.discount ? combination.price : undefined,
  priceValidUntil: combination.discount?.dateEnd,
  deliverytime: combination.deliverytime,
  shippingType: product.shippingType,
  combinationKey: combination.key,
  options: combination.options,
  ean: combination.ean,
  sku: combination.sku,
  mpn: combination.mpn,
  originCountry: combination.originCountry,
  weight: combination.weight,
  stock: combination.stock,
  includeBrand: false,
  withContext: false,
}) as Product

export const SeoProductGroup: React.FC<{ product: tProduct }> = ({ product }) => {
  const description = buildProductDescription(product)
  const shortDescription = [product.descriptionShort, product.descriptionDetails, product.description]
    .find(value => seoText(value))
  const variantDescription = shortDescription ? seoDescription(shortDescription) : ''
  const rawSchemaKeywords = (product.keywords || []).filter(keyword => !TECHNICAL_TAG_REGEX.test(keyword))
  const cleanSchemaKeywords = cleanKeywords(product.keywords)
  const brandName =
    normalizeText(product.brandTitel)
    || getFacetValues(rawSchemaKeywords, 'marke')[0]
    || getFacetValues(rawSchemaKeywords, 'brand')[0]
  const variesBy = getVariantDimensions(product)
  const groupImages = uniqueValues(
    (product.images || [])
      .map(image => getImageUrl(image, product.id, 'xxl'))
      .filter((image): image is string => Boolean(image)),
  )
  const material = variesBy.includes('https://schema.org/material')
    ? undefined
    : getFacetValues(rawSchemaKeywords, 'material')[0]
      || getTechnicalValue(product.technicalData, [/material|stoff|gewebe|holzart/])
  const color = variesBy.includes('https://schema.org/color')
    ? undefined
    : getColorValue({
      keywords: rawSchemaKeywords,
      technicalData: product.technicalData,
      description,
      name: product.name,
    })
  const pattern = variesBy.includes('https://schema.org/pattern')
    ? undefined
    : getFacetValues(rawSchemaKeywords, 'muster')[0]
      || getTechnicalValue(product.technicalData, [/muster|pattern|motiv/])
  const purchasableCombinations = product.combinations.filter(combination =>
    combination.cultivable !== false
    && normalizePrice(combination.discount?.price ?? combination.price) !== undefined,
  )

  const item = compactObject({
    '@context': 'https://schema.org',
    '@type': 'ProductGroup',
    '@id': schemaProductGroupId(product.id, product.path),
    productGroupID: String(product.id),
    url: absoluteProductBaseUrl(product.id, product.path),
    name: product.name,
    description,
    slogan: normalizeText(product.hook),
    image: groupImages,
    keywords: cleanSchemaKeywords.length > 0 ? cleanSchemaKeywords.join(', ') : undefined,
    category:
      normalizeText(product.productType)
      || getFacetValues(rawSchemaKeywords, 'produkttyp')[0]
      || getFacetValues(rawSchemaKeywords, 'kategorie')[0],
    color,
    material,
    pattern,
    brand: brandName ? {
      '@type': 'Brand',
      name: brandName,
    } : undefined,
    variesBy: variesBy.length > 0 ? variesBy : undefined,
    hasVariant: purchasableCombinations.length > 0
      ? purchasableCombinations.map(combination => buildProductGroupVariant(product, combination, description))
      : undefined,
  }) as WithContext<ProductGroup> & { keywords?: string }

  // Google checks the description on each Product. Keep a short, source-backed
  // description there while retaining the full shared text once on the group.
  if (Array.isArray(item.hasVariant)) {
    for (const variant of item.hasVariant) {
      if (!variant || typeof variant !== 'object' || variant['@type'] !== 'Product') continue
      if (variantDescription) variant.description = variantDescription
      for (const key of ['keywords', 'category', 'slogan'] as const) {
        if (typeof item[key] === 'string' && variant[key] === item[key]) delete variant[key]
      }
    }
  }

  return <JsonLd<ProductGroup> item={item} />
}
/* ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
